import { AppContext } from '@/App';
import React, { useContext, useEffect, useState } from 'react'
import { user_columns, User, columnDisplayNames as userColumnDisplayNames } from "@/Components/TeamBuilder/team_table/columns";
import { get_team_columns, TeamMember, columnDisplayNames } from "@/Components/TeamBuilder/team_table/team_columns";
import { ProjectUserRole, TeamMemberColumnVisibility, UserColumnVisibility } from './team_builder';
//import Loading from '@/Components/loading';
import { getRoleIdFromRoleName } from '@/hooks/project-roles';
import { DataTable } from "@/Components/datatable/data-table";
import { ArrowLeftIcon, ArrowRightIcon, ShowControlsIcon } from '@/Components/ui/Icons/TooltipIcon';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog } from '@radix-ui/react-dialog';
import { useResponsiveZoom } from '@/hooks/use-responsive-zoom';
import { DialogContent, DialogHeader, DialogTrigger } from '@/Components/ui/dialog';

interface TeamBuilderProps {
    typeFilter?: "Team Member" | "Viewer" | "Sponsor" | "Project Manager"
}

const TeamBuilder = ({ typeFilter }: TeamBuilderProps) => {
    const app = useContext(AppContext);
    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true); // add a loading state
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>((app.selectedProject ? [...app.sponsors, ...app.projectManagers, ...app.teamMembers] : []).map(user => ({
        ...user,
        projectRole: app.sponsors.find(s => s.id === user.id) ? "Sponsor" :
            app.projectManagers.find(pm => pm.id === user.id) ? "Project Manager" :
                "Team Member"
    })));
    const [userSelection, setUserSelection] = React.useState<{ [key: string]: boolean }>({})
    const [teamMemberSelection, setTeamMemberSelection] = React.useState<{ [key: string]: boolean }>({})
    const [showUserFilters, setShowUserFilters] = useState<boolean>(false);
    const [showTeamFilters, setShowTeamFilters] = useState<boolean>(false);

    useEffect(() => {
        document.body.style.cursor = 'wait';
        fetch("Users/users")
            .then((response) => response.json())
            .then(users => {
                fetch(`Team/${app.selectedProject?.id}/`)
                    .then((response) => response.json())
                    .then((pur: ProjectUserRole[]) => {
                        // add users that are in the team
                        setTeamMembers(users.filter((user: User) => pur.find((p) => p.userId === user.id)).map((user: User) => {
                            const projectUserRole = pur.find((p) => p.userId === user.id);
                            return { ...user, projectRole: projectUserRole ? projectUserRole.roleName as "Team Member" | "Viewer" | "Sponsor" | "Project Manager" : "Team Member" };
                        }));
                        // remove users that are already in the team
                        setUsers(users.filter((user: User) => !pur.find((p) => p.userId === user.id)));
                    })
                    .catch((error) => console.error("Error fetching data:", error));
            })
            .catch((error) => console.error("Error fetching data:", error))
            .finally(() => {
                document.body.style.cursor = 'default';
                setLoading(false);
            });
    }, [app.selectedProject]);

    React.useEffect(() => {
        app.setSponsors(teamMembers.filter((user) => user.projectRole === "Sponsor"));
        app.setProjectManagers(teamMembers.filter((user) => user.projectRole === "Project Manager"));
        app.setTeamMembers(teamMembers.filter((user) => user.projectRole === "Team Member"));
    }, [teamMembers]);

    // if (loading) {
    //     return <Loading />;
    // }

    function loadTeamMemberColumnVisibility(): TeamMemberColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Team Members_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                id: visibilityValues.find((v) => v.columnId === "id")?.isVisible ?? false,
                forename: visibilityValues.find((v) => v.columnId === "forename")?.isVisible ?? app.authLevel <= 3,
                surname: visibilityValues.find((v) => v.columnId === "surname")?.isVisible ?? app.authLevel <= 3,
                displayName: visibilityValues.find((v) => v.columnId === "displayName")?.isVisible ?? true,
                email: visibilityValues.find((v) => v.columnId === "email")?.isVisible ?? true,
                projectRole: typeFilter ? false : true,
            })
        }
        return {
            id: false,
            forename: app.authLevel <= 3,
            surname: app.authLevel <= 3,
            displayName: true,
            email: true,
            projectRole: typeFilter ? false : true,
        }
    }
    function loadUserColumnVisibility(): UserColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Available Users_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                id: visibilityValues.find((v) => v.columnId === "id")?.isVisible ?? false,
                forename: isMobile ? false : visibilityValues.find((v) => v.columnId === "forename")?.isVisible ?? true,
                surname: isMobile ? false : visibilityValues.find((v) => v.columnId === "surname")?.isVisible ?? true,
                displayName: visibilityValues.find((v) => v.columnId === "displayName")?.isVisible ?? true,
                email: visibilityValues.find((v) => v.columnId === "email")?.isVisible ?? true,
            })
        }
        return {
            id: false,
            forename: isMobile ? false : true,
            surname: true,
            displayName: true,
            email: true,
        }
    }

    function HandleAddUserToTeam() {
        const selectedUsers = users.filter((user) => userSelection[String(user.id)]);

        const usersToAdd = selectedUsers.map((user) => ({
            userId: user.id,
            projectId: app.selectedProject?.id,
            roleId: getRoleIdFromRoleName(typeFilter ? typeFilter : "Team Member"), // default role
        }));

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(usersToAdd),
        })
            .then((result) => {
                if (result.ok) {
                    // Add selected users to team members
                    setTeamMembers([...teamMembers, ...selectedUsers.map((user) => ({ ...user, projectRole: typeFilter ? typeFilter : "Team Member" as const }))]);
                    // remove selected users from users
                    setUsers(users.filter((user) => !userSelection[String(user.id)]));
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding users to team: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function HandleRemoveClicked(userId: string) {
        const roles: ProjectUserRole[] = [
            {
                userId: userId,
                projectId: app.selectedProject?.id as number,
                projectName: "", // these values wont be required are we are deleting the user from the team
                roleName: "",
                roleId: 0,
            },
        ];

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.filter((user) => user.id !== userId));
                    const removedUser = teamMembers.find((user) => user.id === userId);
                    if (removedUser) {
                        setUsers([...users, removedUser]);
                        setUserSelection({});
                    }
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error removing user from team: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function HandleGroupRemoveClicked() {
        const roles: ProjectUserRole[] = teamMembers.filter((user) => teamMemberSelection[String(user.id)]).map((user) => ({
            userId: user.id as string,
            projectId: app.selectedProject?.id as number,
            projectName: "", // these values wont be required are we are deleting the user from the team
            roleName: "",
            roleId: 0,
        }));

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.filter((user) => !teamMemberSelection[String(user.id)]));
                    setUsers([...users, ...teamMembers.filter((user) => teamMemberSelection[String(user.id)])]);
                    setTeamMemberSelection({});
                    setUserSelection({});
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error removing users from team: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function HandleSetRoleClicked(userid: string, role: "Viewer" | "Team Member" | "Sponsor" | "Project Manager") {
        const roles: ProjectUserRole[] = [
            {
                userId: userid,
                projectId: app.selectedProject?.id as number,
                projectName: app.selectedProject?.name,
                roleName: role,
                roleId: getRoleIdFromRoleName(role),
            },
        ];

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.map((user) => {
                        if (user.id === userid) {
                            return { ...user, projectRole: role };
                        }
                        return user;
                    }));
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error setting role: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function HandleGroupSetRoleClicked(role: "Viewer" | "Team Member" | "Sponsor" | "Project Manager") {
        const roles: ProjectUserRole[] = teamMembers.filter((user) => teamMemberSelection[String(user.id)]).map((user) => ({
            userId: user.id as string,
            projectId: app.selectedProject?.id as number,
            projectName: app.selectedProject?.name,
            roleName: role,
            roleId: getRoleIdFromRoleName(role),
        }));

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.map((user) => {
                        if (teamMemberSelection[String(user.id)]) {
                            return { ...user, projectRole: role };
                        }
                        return user;
                    }));
                    setTeamMemberSelection({});
                    setUserSelection({});
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error setting role: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    const teamMembersGroupActions = [
        {
            name: "Remove",
            action: HandleGroupRemoveClicked,
        }
    ];

    if (!typeFilter) {
        teamMembersGroupActions.push(
            {
                name: "Set Role - Viewer",
                action: () => HandleGroupSetRoleClicked("Viewer"),
            },
            {
                name: "Set Role - Team Member",
                action: () => HandleGroupSetRoleClicked("Team Member"),
            },
            {
                name: "Set Role - Sponsor",
                action: () => HandleGroupSetRoleClicked("Sponsor"),
            },
            {
                name: "Set Role - Project Manager",
                action: () => HandleGroupSetRoleClicked("Project Manager"),
            });
    }

    if (isMobile) {
        return (
            <div className={"flex flex-col w-full"}>
                <h1 className="flex flex-row text-4xl font-bold mx-auto">
                    Assigned
                    <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowTeamFilters(!showTeamFilters)}>
                        <ShowControlsIcon className='h-7 w-7' />
                    </div>
                </h1>
                <DataTable
                    columns={get_team_columns(HandleSetRoleClicked, HandleRemoveClicked, typeFilter)}
                    data={teamMembers}
                    setData={setTeamMembers}
                    loading={loading}
                    viewOnly={app.authLevel < 3}
                    rowSelection={teamMemberSelection}
                    setRowSelection={setTeamMemberSelection}
                    groupActions={app.authLevel > 3 ? teamMembersGroupActions : undefined}
                    columnVisibility={loadTeamMemberColumnVisibility()}
                    table_title="Team Members"
                    columnDisplayNames={columnDisplayNames}
                    passedColumnFilters={typeFilter ? [{ id: "projectRole", value: typeFilter }] : undefined}
                    showFilters={showTeamFilters}
                />
                <button disabled={Object.keys(teamMemberSelection).length === 0} className="p-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={HandleGroupRemoveClicked}>Remove From Team</button>

                <Dialog>
                    <DialogTrigger>
                        {app.authLevel > 3 && (<button className="w-full mt-2 p-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50">Add Team Member</button>)}
                    </DialogTrigger>
                    <DialogContent className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }}>
                        <DialogHeader>
                            <div className="flex flex-row text-4xl font-bold">
                                <div>Available Users</div>
                                <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer"
                                    onClick={() => setShowUserFilters(!showUserFilters)}>
                                    <ShowControlsIcon className='h-7 w-7' />
                                </div>
                            </div>
                        </DialogHeader>
                        <DataTable
                            columns={user_columns}
                            data={users}
                            setData={setUsers}
                            loading={loading}
                            viewOnly={app.authLevel < 3}
                            rowSelection={userSelection}
                            setRowSelection={setUserSelection}
                            table_title="Available Users"
                            columnVisibility={loadUserColumnVisibility()}
                            columnDisplayNames={userColumnDisplayNames}
                            showFilters={showUserFilters}
                        />
                        <button disabled={Object.keys(userSelection).length === 0} className="p-2 mb-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={HandleAddUserToTeam}>Add To Team</button>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    return (
        <div className="flex flex-row justify-between">
            {app.authLevel > 3 && (
                <><div className="flex flex-col w-full">
                    <h1 className="flex flex-row text-lg font-bold mx-auto">
                        Available Users
                        <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowUserFilters(!showUserFilters)}><ShowControlsIcon className='h-4 w-4' /></div>
                    </h1>
                    <DataTable
                        columns={user_columns}
                        data={users}
                        setData={setUsers}
                        loading={loading}
                        viewOnly={app.authLevel < 3}
                        rowSelection={userSelection}
                        setRowSelection={setUserSelection}
                        table_title="Available Users"
                        columnVisibility={loadUserColumnVisibility()}
                        columnDisplayNames={userColumnDisplayNames}
                        showFilters={showUserFilters}
                    />
                </div>
                    <div className="flex flex-col w-[4%] items-center justify-center">
                        <button disabled={Object.keys(userSelection).length === 0} className="p-2 mb-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={HandleAddUserToTeam}><ArrowRightIcon /></button>
                        <button disabled={Object.keys(teamMemberSelection).length === 0} className="p-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={HandleGroupRemoveClicked}><ArrowLeftIcon /></button>
                    </div>
                </>
            )}
            <div className={"flex flex-col " + (app.authLevel <= 3 ? " mx-auto" : " w-full")}>
                <h1 className="flex flex-row text-lg font-bold mx-auto">
                    Assigned
                    <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowTeamFilters(!showTeamFilters)}>
                        <ShowControlsIcon className='h-4 w-4' />
                    </div>
                </h1>
                <DataTable
                    columns={get_team_columns(HandleSetRoleClicked, HandleRemoveClicked, typeFilter)}
                    data={teamMembers}
                    setData={setTeamMembers}
                    loading={loading}
                    viewOnly={app.authLevel < 3}
                    rowSelection={teamMemberSelection}
                    setRowSelection={setTeamMemberSelection}
                    groupActions={app.authLevel > 3 ? teamMembersGroupActions : undefined}
                    columnVisibility={loadTeamMemberColumnVisibility()}
                    table_title="Team Members"
                    columnDisplayNames={columnDisplayNames}
                    passedColumnFilters={typeFilter ? [{ id: "projectRole", value: typeFilter }] : undefined}
                    showFilters={showTeamFilters}
                />
            </div>
        </div>
    )
}

export default TeamBuilder